// Global Types
import type {
  $Translate,
} from 'common.language';
import type {
  $FullChampionshipAdminRequest,
} from 'common.data';

// Global Enums
import {
  ChampionshipAdminRequestStatus,
} from 'common.data';

// Global Components
import * as React from 'react';
import {
  Button,
  Card,
  CardBody,
  Table,
} from 'reactstrap';
import {
  CommonDialog,
  DeleteDialog,
  Loader,
  SectionHeader,
  TdDate,
} from '@vroskus/library-components';

// Helpers
import {
  cleanFormValues,
  isDiff,
} from '@vroskus/library-helpers/dist/browser';
import {
  defaultDeleteItem,
  defaultGetData,
  defaultUpsertItem,
} from '../../../../helpers';

// Components

// Services
import Services from '../../../../services';

// Types
import type {
  $ReducerState,
} from '../../../../redux';

export type $OwnProps = $Translate & {
  id: string;
};

export type $StateProps = {
  main: $ReducerState<'main'>;
};

export type $DispatchProps = Record<string, unknown>;

type $Props = $DispatchProps & $OwnProps & $StateProps;

type $State = {
  data: Array<$FullChampionshipAdminRequest>;
  loading: boolean;
};

class ChampionshipAdminRequestsSectionView extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      data: [],
      loading: false,
    };
  }

  componentDidMount(): void {
    this.getData();
    this.setupSocket();
  }

  componentDidUpdate(prevProps: $Props): void {
    const {
      main,
    } = this.props;

    if (isDiff(
      main.activeRegionSeasonId,
      prevProps.main.activeRegionSeasonId,
    )) {
      this.getData();
    }
  }

  setupSocket(): void {
    Services.Socket.on({
      action: (data) => {
        const {
          main,
        } = this.props;

        if (main.activeRegionSeasonId === data.RegionSeasonId && data.modelName === 'ChampionshipAdminRequest') {
          this.getData();
        }
      },
      type: Services.Socket.type.modelChangedEvent,
    });
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  async getData(): Promise<void> {
    const {
      main,
    } = this.props;

    const RegionSeasonId = main.activeRegionSeasonId;

    await defaultGetData({
      action: async () => {
        if (RegionSeasonId !== null) {
          await this.setStateAsync({
            loading: true,
          });

          const data = await Services.Api.getManyChampionshipAdminRequest({
            RegionSeasonId,
          });

          await this.setStateAsync({
            data,
            loading: false,
          });
        }
      },
      errorAction: async () => this.stopLoader(),
    });
  }

  async stopLoader(): Promise<void> {
    await this.setStateAsync({
      loading: false,
    });
  }

  renderDataItem(dataItem: $FullChampionshipAdminRequest) {
    const {
      t,
    } = this.props;

    const {
      id,
    } = dataItem;

    return (
      <tr key={dataItem.id}>
        <TdDate
          duration
          multiple
          value={dataItem.createdAt}
        />
        <td>{dataItem.Championship.Name}</td>
        <td>{dataItem.Name}</td>
        <td>{dataItem.Email}</td>
        <td>
          <div className={'d-flex justify-content-end'}>
            <CommonDialog
              content={(
                <CardBody>
                  {t('Do you really want to add this user to admins?')}
                </CardBody>
              )}
              id={`confirm-championship-admin-request-dialog-${id}`}
              labelCloseButton={t('Close')}
              labelConfirmButton={t('Confirm')}
              onAction={() => defaultUpsertItem({
                action: async () => {
                  const params = cleanFormValues<$FullChampionshipAdminRequest>({
                    ...dataItem,
                    Status: ChampionshipAdminRequestStatus.confirmed,
                  });

                  await Services.Api.upsertChampionshipAdminRequest(params);
                },
              })}
              title={t('Need confirmation')}
            >
              <Button
                className={'mr-1'}
                color={'warning'}
                id={`confirm-championship-admin-request-button-${id}`}
                outline
                size={'sm'}
              >
                {t('Confirm')}
              </Button>
            </CommonDialog>
            <DeleteDialog
              content={t('Do you really want to delete?')}
              id={`delete-championship-admin-request-dialog-${id}`}
              labelCloseButton={t('Close')}
              labelDeleteButton={t('Delete')}
              onAction={() => defaultDeleteItem({
                action: async () => Services.Api.deleteChampionshipAdminRequest(dataItem),
              })}
              subTitle={''}
              title={t('Championship admin request')}
            >
              <Button
                color={'danger'}
                id={`delete-championship-admin-request-button-${id}`}
                outline
                size={'sm'}
              >
                {t('Delete request')}
              </Button>
            </DeleteDialog>
          </div>
        </td>
      </tr>
    );
  }

  renderData() {
    const {
      data,
    } = this.state;

    return (
      <>
        {data.map((dataItem: $FullChampionshipAdminRequest) => this.renderDataItem(dataItem))}
      </>
    );
  }

  renderHeader() {
    const {
      t,
    } = this.props;

    return (
      <SectionHeader
        bottomBorder
        title={t('Pending championship admin requests')}
      />
    );
  }

  renderTable() {
    const {
      t,
    } = this.props;

    return (
      <Table responsive>
        <thead>
          <tr>
            <th>{t('Date')}</th>
            <th>{t('Championship')}</th>
            <th>{t('Name')}</th>
            <th>{t('Email')}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {this.renderData()}
        </tbody>
      </Table>
    );
  }

  renderContent() {
    const {
      loading,
    } = this.state;

    return (
      <Loader
        loading={loading === true}
        overlay
      >
        {this.renderTable()}
      </Loader>
    );
  }

  render() {
    const {
      id,
    } = this.props;
    const {
      data,
    } = this.state;

    return (data.length > 0 ? (
      <Card id={id}>
        {this.renderHeader()}
        {this.renderContent()}
      </Card>
    ) : null);
  }
}

export default ChampionshipAdminRequestsSectionView;
